import React, { useState, useRef, useEffect } from 'react';
import { saveAs } from 'file-saver';
import './App.css';

const ASCII_CHARS = ['@', '#', 'S', '%', '?', '*', '+', ';', ':', ',', '.', ' '];

function App() {
  const [asciiArt, setAsciiArt] = useState('');
  const [fileName, setFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [contrast, setContrast] = useState(5);
  const [density, setDensity] = useState(5);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);

  const resizeImage = (image, newWidth = 100) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const aspectRatio = image.height / image.width;
    const newHeight = Math.round(aspectRatio * newWidth * 0.55);

    canvas.width = newWidth;
    canvas.height = newHeight;

    ctx.drawImage(image, 0, 0, newWidth, newHeight);
    return ctx.getImageData(0, 0, newWidth, newHeight);
  };

  const toGrayscale = (imageData) => {
    const grayscaleData = new Uint8ClampedArray(imageData.data.length / 4);
    for (let i = 0; i < imageData.data.length; i += 4) {
      const avg = (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) / 3;
      grayscaleData[i / 4] = avg;
    }
    return grayscaleData;
  };

  const pixelToAscii = (grayValue) => {
    return ASCII_CHARS[Math.floor((grayValue * ASCII_CHARS.length) / 256)];
  };

  const generateAsciiArt = (image) => {
    setIsLoading(true);
    const newWidth = Math.floor(100 * (density / 5)); // Adjust width based on density
    const resizedImageData = resizeImage(image, newWidth);
    const grayscaleData = toGrayscale(resizedImageData);
    
    let asciiImage = '';
    for (let i = 0; i < grayscaleData.length; i++) {
      if (i % newWidth === 0 && i !== 0) asciiImage += '\n';
      let pixel = grayscaleData[i];
      // Apply contrast
      pixel = Math.pow((pixel / 255), contrast) * 255;
      asciiImage += pixelToAscii(pixel);
    }

    setAsciiArt(asciiImage);
    setIsLoading(false);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => generateAsciiArt(img);
        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([asciiArt], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `${fileName.split('.')[0]}_ascii.txt`);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(asciiArt).then(() => {
      alert('ASCII art copied to clipboard!');
    });
  };

  const handleReset = () => {
    setAsciiArt('');
    setFileName('');
    setContrast(5);
    setDensity(5);
    setZoom(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleZoomIn = () => {
    setZoom(prevZoom => Math.min(prevZoom + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoom(prevZoom => Math.max(prevZoom - 0.1, 0.5));
  };

  useEffect(() => {
    if (previewRef.current) {
      previewRef.current.style.transform = `scale(${zoom})`;
      previewRef.current.style.transformOrigin = 'top left';
    }
  }, [zoom]);

  return (
    <div className="container">
      <h1>ASCII Art Generator</h1>
      <p className="tagline">Convert your images into stunning ASCII art effortlessly.</p>
      <div className="upload-area" onClick={() => fileInputRef.current.click()}>
        <input 
          type="file" 
          accept="image/*" 
          onChange={handleImageUpload} 
          ref={fileInputRef}
          style={{display: 'none'}}
        />
        <p>Drag and drop an image here or click to upload</p>
      </div>
      <div className="preview-title">Preview</div>
      <div className="preview-container">
        <div className="preview" ref={previewRef}>
          {isLoading ? 'Generating ASCII art...' : asciiArt}
        </div>
      </div>
      <div className="control-panel">
        <div>
          <label htmlFor="contrast">Contrast:</label>
          <input 
            type="range" 
            id="contrast" 
            min="1" 
            max="10" 
            value={contrast}
            onChange={(e) => setContrast(Number(e.target.value))}
          />
        </div>
        <div>
          <label htmlFor="density">Density:</label>
          <input 
            type="range" 
            id="density" 
            min="1" 
            max="10" 
            value={density}
            onChange={(e) => setDensity(Number(e.target.value))}
          />
        </div>
        <div>
          <label>Zoom:</label>
          <button onClick={handleZoomOut}>-</button>
          <span>{Math.round(zoom * 100)}%</span>
          <button onClick={handleZoomIn}>+</button>
        </div>
      </div>
      <div className="button-group">
        <button onClick={() => fileInputRef.current.click()}>Upload New Image</button>
        <button onClick={handleReset}>Reset</button>
        <button onClick={handleDownload}>Download ASCII Art</button>
        <button onClick={handleCopyToClipboard}>Copy to Clipboard</button>
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}

export default App;